import { lazy } from 'react';

/**
 * Drop-in replacement for React.lazy that transparently retries the dynamic
 * import a few times before failing.
 *
 * Mobile browsers (especially iOS Safari) frequently drop the request for a
 * lazy JS/CSS chunk on the FIRST navigation to a page — the import() rejects
 * with a ChunkLoadError, React surfaces it to the ErrorBoundary, and only a
 * manual/auto reload fixes it. Retrying the import with a short backoff lets
 * that transient failure recover silently inside the Suspense boundary, so the
 * user never sees the "hiccup" error screen and no full-page reload is needed.
 *
 * If every retry still fails (e.g. a genuinely stale deploy where the chunk no
 * longer exists), the last error is rethrown and the global ErrorBoundary's
 * one-time reload kicks in as the final fallback.
 *
 * @param {() => Promise<{ default: React.ComponentType<any> }>} importer
 * @param {number} retries   number of extra attempts after the first (default 3)
 * @param {number} intervalMs base backoff between attempts, grows linearly
 */
export default function lazyWithRetry(importer, retries = 3, intervalMs = 400) {
  return lazy(async () => {
    let lastError;
    for (let attempt = 0; attempt <= retries; attempt += 1) {
      try {
        return await importer();
      } catch (error) {
        lastError = error;
        if (attempt < retries) {
          // Linear backoff: 400ms, 800ms, 1200ms — gives a flaky mobile
          // connection time to recover before the next attempt.
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, intervalMs * (attempt + 1)));
        }
      }
    }
    throw lastError;
  });
}
